import { useEffect, useState } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import type { MigrationRowData, TargetSummary } from "./utils/constants";

interface Props {
  open: boolean;
  row: MigrationRowData | null;
  targets: TargetSummary[];
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

export default function MarkAppliedModal({ open, row, targets, onCancel, onConfirm }: Props) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onCancel]);

  useEffect(() => { if (open) setReason(""); }, [open]);

  if (!open || !row) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={onCancel}>
      <div
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        className="bg-white border border-slate-200 rounded-xl shadow-2xl max-w-md w-full overflow-hidden"
      >
        <div className="p-5">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center shrink-0">
              <ExclamationTriangleIcon className="w-5 h-5 text-amber-600" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-bold text-slate-900">Mark as applied?</h2>
              <p className="text-[11px] text-slate-500 mt-1">
                This records the migration as already applied without running its SQL.
                Use only when you applied it manually outside the UI.
              </p>
            </div>
          </div>

          <div className="mt-4 p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Migration</div>
            <div className="text-[11px] text-slate-700 font-mono break-all">
              {row.folderName}/{row.fileName}{" "}
              <span className="text-blue-600 font-bold">v{row.version}</span>
            </div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 pt-1">
              Targets ({targets.length})
            </div>
            <ul className="text-[11px] text-slate-700 space-y-0.5 max-h-32 overflow-y-auto">
              {targets.map((t) => (
                <li key={t.id}>
                  <span className="font-mono text-slate-700">{t.databaseName}</span>{" "}
                  <span className="text-slate-400">— {t.name}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-4">
            <label className="block text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
              Reason (optional)
            </label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Applied via mysql CLI on 2026-06-05"
              className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-[12px] text-slate-700 placeholder:text-slate-400 focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none"
            />
          </div>
        </div>

        <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3.5 py-2 text-sm font-medium bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason.trim() || "Marked applied manually")}
            className="px-3.5 py-2 text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white rounded-lg"
          >
            Mark applied
          </button>
        </div>
      </div>
    </div>
  );
}
